/**
 * Sign — Öffentliche Unterschriften-Seite (Issue #649)
 *
 * Route: /sign/:token
 * Kein Auth erforderlich — zugänglich über den per Mail versendeten Link.
 *
 * Ablauf:
 *  1. Token aus URL laden → POST /api/signature-request { action: 'getByToken', token }
 *  2. Zeigt Vertrag-PDF (falls vorhanden) und Unterschriften-Pad
 *  3. Submit → POST /api/signature-request { action: 'sign', token, signature_data }
 *  4. Bestätigungsscreen anzeigen
 */

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'wouter';
import SignaturePad from 'signature_pad';
import { Car, Check, Loader2, RotateCcw, AlertCircle } from 'lucide-react';

// ── Types ──

interface SigningRequest {
  id: string;
  status: 'ausstehend' | 'unterschrieben' | 'abgelaufen';
  vertrag_pdf_url: string | null;
  already_signed: boolean;
  mietwagen_akte: {
    id: string;
    akten_nummer: string | null;
    akten_nr: string | null;
    kunde_name: string | null;
  } | null;
}

type Phase = 'loading' | 'error' | 'already_signed' | 'form' | 'submitting' | 'success';

// ── Signature Pad Component ──

function SignatureCanvas({
  onReady,
}: {
  onReady: (pad: SignaturePad) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d')?.scale(ratio, ratio);
    const pad = new SignaturePad(canvas, { backgroundColor: 'rgb(255,255,255)' });
    onReady(pad);
    return () => pad.off();
  }, [onReady]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full touch-none"
      style={{ cursor: 'crosshair' }}
    />
  );
}

// ── Main Component ──

export default function Sign() {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [request, setRequest] = useState<SigningRequest | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const padRef = useRef<SignaturePad | null>(null);

  useEffect(() => {
    if (!token) {
      setErrorMsg('Ungültiger Signing-Link — Token fehlt.');
      setPhase('error');
      return;
    }

    (async () => {
      try {
        const res = await fetch('/api/signature-request', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getByToken', token }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setErrorMsg((err as Record<string, string>).error || 'Signing-Link nicht gefunden.');
          setPhase('error');
          return;
        }
        const data = (await res.json()) as SigningRequest;
        setRequest(data);
        if (data.already_signed) {
          setPhase('already_signed');
        } else if (data.status === 'abgelaufen') {
          setErrorMsg('Dieser Signing-Link ist abgelaufen.');
          setPhase('error');
        } else {
          setPhase('form');
        }
      } catch {
        setErrorMsg('Verbindungsfehler — bitte Seite neu laden.');
        setPhase('error');
      }
    })();
  }, [token]);

  async function handleSubmit() {
    if (!padRef.current || padRef.current.isEmpty()) {
      alert('Bitte unterschreiben Sie im Feld unten.');
      return;
    }
    setPhase('submitting');
    const signatureData = padRef.current.toDataURL('image/png');
    try {
      const res = await fetch('/api/signature-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'sign', token, signature_data: signatureData }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setErrorMsg((err as Record<string, string>).error || 'Fehler beim Speichern der Unterschrift.');
        setPhase('error');
        return;
      }
      setPhase('success');
    } catch {
      setErrorMsg('Verbindungsfehler — bitte erneut versuchen.');
      setPhase('form');
    }
  }

  const kundeName = request?.mietwagen_akte?.kunde_name;
  const aktenNummer =
    request?.mietwagen_akte?.akten_nummer ||
    request?.mietwagen_akte?.akten_nr ||
    null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start px-4 py-10">
      {/* Header */}
      <div className="w-full max-w-lg mb-8 text-center">
        <div className="inline-flex items-center gap-2 justify-center mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-gray-900">GutachterKoop</span>
        </div>
        <h1 className="text-xl font-semibold text-gray-800">Mietvertrag unterzeichnen</h1>
        {kundeName && (
          <p className="text-sm text-gray-500 mt-1">{kundeName}</p>
        )}
        {aktenNummer && (
          <p className="text-xs text-gray-400 mt-0.5 font-mono">Akte: {aktenNummer}</p>
        )}
      </div>

      <div className="w-full max-w-lg">
        {/* Loading */}
        {phase === 'loading' && (
          <div className="flex flex-col items-center gap-3 py-16 text-gray-500">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            <span className="text-sm">Vertrag wird geladen…</span>
          </div>
        )}

        {/* Error */}
        {phase === 'error' && (
          <div className="bg-white rounded-xl border border-red-200 p-8 text-center shadow-sm">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <p className="text-base font-semibold text-gray-800 mb-2">Link nicht verfügbar</p>
            <p className="text-sm text-gray-500">{errorMsg}</p>
          </div>
        )}

        {/* Already signed */}
        {phase === 'already_signed' && (
          <div className="bg-white rounded-xl border border-emerald-200 p-8 text-center shadow-sm">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-emerald-600" />
            </div>
            <p className="text-base font-semibold text-gray-800 mb-2">Bereits unterzeichnet</p>
            <p className="text-sm text-gray-500">
              Dieser Mietvertrag wurde bereits von Ihnen unterzeichnet. Vielen Dank!
            </p>
          </div>
        )}

        {/* Form */}
        {(phase === 'form' || phase === 'submitting') && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* PDF Preview */}
            {request?.vertrag_pdf_url && (
              <div className="border-b border-gray-100">
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Mietvertrag</p>
                </div>
                <div className="h-64 sm:h-96">
                  <iframe
                    src={request.vertrag_pdf_url}
                    className="w-full h-full"
                    title="Mietvertrag PDF"
                  />
                </div>
              </div>
            )}

            {/* Signature Pad */}
            <div className="px-5 pt-5 pb-4">
              <p className="text-sm font-semibold text-gray-700 mb-1">Unterschrift</p>
              <p className="text-xs text-gray-400 mb-3">
                Bitte unterschreiben Sie mit dem Finger oder der Maus im Feld unten.
              </p>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50"
                style={{ height: 160 }}
              >
                <SignatureCanvas
                  onReady={(pad) => {
                    padRef.current = pad;
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => padRef.current?.clear()}
                className="mt-2 inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                <RotateCcw className="w-3 h-3" />
                Unterschrift löschen
              </button>
            </div>

            {/* Submit */}
            <div className="px-5 pb-5">
              <p className="text-xs text-gray-400 mb-4">
                Mit Ihrer Unterschrift bestätigen Sie die Bedingungen des Mietvertrags.
              </p>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={phase === 'submitting'}
                className="w-full py-3 px-4 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2"
              >
                {phase === 'submitting' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Wird gespeichert…
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Jetzt verbindlich unterschreiben
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Success */}
        {phase === 'success' && (
          <div className="bg-white rounded-xl border border-emerald-200 p-8 text-center shadow-sm">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-emerald-600" />
            </div>
            <p className="text-lg font-semibold text-gray-800 mb-2">
              Vielen Dank!
            </p>
            <p className="text-sm text-gray-500">
              Ihre Unterschrift wurde erfolgreich gespeichert. Sie erhalten in Kürze eine Bestätigung.
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <p className="text-xs text-gray-400 mt-10">
        GutachterKoop · <a href="https://gutachter-koop.de" className="underline">gutachter-koop.de</a>
      </p>
    </div>
  );
}
